import { PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigateWithSearchParams } from "@p0security/frontend-core";
import { ErrorDisplay } from "@p0security/frontend-core/react";
import { AppPaths } from "@p0security/shared/routes/constants";
import { Policy } from "@p0security/shared/types/policy/types";
import { Alert, App, Flex, Spacing, Spin } from "@p0security/ui";
import yaml from "js-yaml";
import stringify from "json-stable-stringify";
import { isEqual, isUndefined, omitBy } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePolicyStore } from "store/policyStore";

import CodeEditor from "../Jit/Policy/CodeEditor";
import { AccessEditor } from "../Jit/Policy/components/editors/AccessEditor";
import { RequestorEditor } from "../Jit/Policy/components/editors/RequestorEditor";
import {
  DEFAULT_NEW_REQUESTOR,
  DEFAULT_NEW_RESOURCE,
} from "../Jit/Policy/constants";
import { InstalledIntegrationsProvider } from "../Jit/Policy/providers/InstalledIntegrationsProvider";
import {
  PolicyEditorContext,
  RoutingRuleEditorProvider,
} from "../Jit/Policy/store/RoutingRuleEditorContext";
import { useLoadPolicies } from "../Jit/Policy/useLoadPolicies";
import { useSubmitWorkflow } from "../Jit/Policy/useSubmitWorkflow";
import { DEFAULT_RULE, EXAMPLE_RULE, hashRule } from "../Jit/Policy/utils";
import { PolicyChangeWarning } from "../Policies/PolicyChangeWarning";
import { PolicyOutlineNav } from "../Policies/PolicyOutlineNav";
import { SECTION_TITLES } from "../Policies/constants";
import { EditorHeader, type EditorMode } from "./EditorHeader";
import { EditorStepper } from "./EditorStepper";
import { ResourceEditor, type ResourceMode } from "./ResourceEditor";
import { toPrefill, toResourceMode } from "./utils/prefill";

const isPolicy = (value: unknown): value is Policy =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  "requestor" in value &&
  "resource" in value &&
  "approval" in value;

const PolicyEditorPageInner: React.FC<{
  initialRule?: Policy;
  ruleIndex?: number;
  initialResourceMode?: ResourceMode;
}> = ({ initialRule, ruleIndex, initialResourceMode }) => {
  const navigate = useNavigate();
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const { orgSlug } = useParams();
  const { modal } = App.useApp();
  const submitWorkflow = useSubmitWorkflow();
  const {
    currentVersion,
    errors,
    isSubmitting,
    jsonRules,
    selectedYamlRule,
    setErrors,
    setSelectedRule,
  } = usePolicyStore();

  const {
    name,
    disabled,
    resource,
    requestor,
    approval,
    setName,
    setDisabled,
    isValidRule,
    isComponentValid,
  } = useContext(PolicyEditorContext);

  const isEditMode = initialRule !== undefined;

  // useLayoutEffect, not useEffect — seed the store before paint to avoid a
  // flash of empty YAML when toggling to advanced mode.
  useLayoutEffect(() => {
    setSelectedRule(initialRule ?? {});
  }, [initialRule, setSelectedRule]);

  const [mode, setMode] = useState<EditorMode>("guided");

  const [yamlDirty, setYamlDirty] = useState(false);
  const [resourceFilterEditInProgress, setResourceFilterEditInProgress] =
    useState(false);
  const [accessRuleEditInProgress, setAccessRuleEditInProgress] =
    useState(false);
  const [resourceSelectionIncomplete, setResourceSelectionIncomplete] =
    useState(false);

  const hasFormChanges = useMemo(() => {
    if (!isEditMode) {
      return !!(
        name ||
        disabled ||
        (resource && !isEqual(resource, DEFAULT_NEW_RESOURCE)) ||
        (requestor && !isEqual(requestor, DEFAULT_NEW_REQUESTOR)) ||
        (approval && approval.length > 0)
      );
    }
    return !isEqual(
      omitBy(
        {
          ...initialRule,
          name: initialRule.name || undefined,
          disabled: initialRule.disabled ?? false,
        },
        isUndefined
      ),
      omitBy(
        {
          name: name || undefined,
          disabled: disabled ?? false,
          requestor,
          resource,
          approval,
        },
        isUndefined
      )
    );
  }, [isEditMode, initialRule, name, disabled, requestor, resource, approval]);

  const isDirty = yamlDirty || hasFormChanges;
  const isEditing = resourceFilterEditInProgress || accessRuleEditInProgress;

  const isValid = isValidRule;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty || isEditing) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isEditing]);

  const exitToStudio = useCallback(
    () => navigateWithSearchParams(`/o/${orgSlug}/${AppPaths.Policies}`),
    [navigateWithSearchParams, orgSlug]
  );

  const handleCancel = useCallback(() => {
    if (isDirty || isEditing) {
      modal.confirm({
        title: "You have unsaved changes",
        content: "Are you sure you want to leave? Your changes will be lost.",
        okText: "Discard",
        okType: "danger",
        cancelText: "Keep editing",
        onOk: exitToStudio,
      });
    } else {
      exitToStudio();
    }
  }, [isDirty, isEditing, modal, exitToStudio]);

  const handleBundleRedirect = useCallback(() => {
    navigate("../bundles/new", {
      state: { prefill: { name, disabled, requestor, approval } },
    });
  }, [navigate, name, disabled, requestor, approval]);

  const handleModeChange = useCallback(
    (value: EditorMode) => {
      if (value === "advanced") {
        if (requestor && resource && approval && approval.length > 0) {
          setSelectedRule({
            name: name || undefined,
            disabled: disabled ?? false,
            requestor,
            resource,
            approval,
          });
        } else if (initialRule) {
          setSelectedRule(initialRule);
        }
      }
      setMode(value);
    },
    [
      approval,
      disabled,
      initialRule,
      name,
      requestor,
      resource,
      setSelectedRule,
    ]
  );

  const setDefaultRule = useCallback(() => {
    setSelectedRule(DEFAULT_RULE);
  }, [setSelectedRule]);

  const setSampleExample = useCallback(() => {
    setSelectedRule(EXAMPLE_RULE);
  }, [setSelectedRule]);

  const upsertRule = useCallback(
    async (rule: Policy) => {
      const ruleHash = stringify(rule);
      const updatedRules = [...jsonRules];

      if (isEditMode && ruleIndex !== undefined) {
        if (
          updatedRules.some(
            (r, i) => i !== ruleIndex && stringify(r) === ruleHash
          )
        ) {
          setErrors(["This rule already exists"]);
          return false;
        }
        updatedRules[ruleIndex] = rule;
      } else {
        if (jsonRules.some((r) => stringify(r) === ruleHash)) {
          setErrors(["This rule already exists"]);
          return false;
        }
        updatedRules.push(rule);
      }

      const policyLabel = rule.name ? `Policy "${rule.name}"` : "Policy";
      try {
        await submitWorkflow({
          jsonData: { currentVersion, policy: { rules: updatedRules } },
          shouldNavigate: false,
          successMessage: `${policyLabel} was successfully saved.`,
        });
        return usePolicyStore.getState().errors.length === 0;
      } catch (err) {
        setErrors([err instanceof Error ? err.message : String(err)]);
        return false;
      }
    },
    [
      isEditMode,
      ruleIndex,
      jsonRules,
      currentVersion,
      submitWorkflow,
      setErrors,
    ]
  );

  const handleSave = useCallback(async () => {
    if (!isValid || !requestor || !approval || !resource) return;
    setErrors([]);

    const rule = { name, disabled, requestor, resource, approval };
    const success = await upsertRule(rule);
    if (success) {
      exitToStudio();
    }
  }, [
    isValid,
    resource,
    requestor,
    approval,
    setErrors,
    name,
    disabled,
    upsertRule,
    exitToStudio,
  ]);

  const handleYamlSubmit = useCallback(
    async (yamlString: string) => {
      setErrors([]);
      let parsed: unknown;
      try {
        parsed = yaml.load(yamlString);
      } catch (err) {
        setErrors([`YAML parse error: ${String(err)}`]);
        return;
      }
      if (!isPolicy(parsed)) {
        setErrors([
          "YAML must define a policy with requestor, resource, and approval fields.",
        ]);
        return;
      }
      const success = await upsertRule(parsed);
      if (success) {
        exitToStudio();
      }
    },
    [upsertRule, setErrors, exitToStudio]
  );

  return (
    <Spacing asChild mx="auto">
      <Flex
        vertical
        className="max-w-[min(1440px,calc(100vw-48px))] pb-8 w-full"
      >
        <EditorHeader
          name={name ?? ""}
          disabled={disabled ?? false}
          isDirty={isDirty}
          isValid={isValid}
          isSubmitting={isSubmitting}
          mode={mode}
          onNameChange={setName}
          onDisabledChange={setDisabled}
          onSave={handleSave}
          onCancel={handleCancel}
          onModeChange={handleModeChange}
          namePlaceholder="Name (optional)"
          untitledLabel="Untitled policy"
          saveIcon={isEditMode ? <SaveOutlined /> : <PlusOutlined />}
          saveLabel={isEditMode ? "Save Policy" : "Create Policy"}
          enabledTooltip="Toggle policy. Disabled policies are not evaluated."
          saveAdornment={isEditMode ? <PolicyChangeWarning /> : undefined}
        />

        {errors.map((e, ix) => (
          <ErrorDisplay error={e} key={ix} />
        ))}

        <PolicyOutlineNav />

        <Flex vertical gap={16}>
          {mode === "advanced" ? (
            <CodeEditor
              onDirty={setYamlDirty}
              handleSubmitWorkflow={handleYamlSubmit}
              rule={selectedYamlRule}
              setDefaultRule={setDefaultRule}
              setSampleExample={setSampleExample}
            />
          ) : (
            <EditorStepper
              sections={[
                {
                  title: SECTION_TITLES.requestor,
                  valid: isComponentValid("requestor"),
                  touched: isEditMode || requestor !== undefined,
                  children: <RequestorEditor />,
                },
                {
                  title: SECTION_TITLES.resource,
                  valid:
                    isComponentValid("resource") &&
                    !resourceSelectionIncomplete,
                  touched: isEditMode || resource !== undefined,
                  children: (
                    <ResourceEditor
                      onResourceFilterEditInProgress={
                        setResourceFilterEditInProgress
                      }
                      onSelectionIncomplete={setResourceSelectionIncomplete}
                      onBundleRedirect={handleBundleRedirect}
                      initialMode={initialResourceMode}
                    />
                  ),
                },
                {
                  title: SECTION_TITLES.approval,
                  valid: isComponentValid("approval"),
                  touched: approval.length > 0,
                  children: (
                    <AccessEditor
                      onAccessRuleEditInProgress={setAccessRuleEditInProgress}
                      defaultOpen
                    />
                  ),
                },
              ]}
            />
          )}
        </Flex>
      </Flex>
    </Spacing>
  );
};

export const PolicyEditorPage: React.FC = () => {
  const { ruleId } = useParams<{ ruleId?: string }>();
  const location = useLocation();

  const { currentVersion, errors, isLoading, jsonRules } = usePolicyStore();
  const isEditPolicy = ruleId !== undefined;

  useLoadPolicies({ force: isEditPolicy });

  const alreadyLoaded = currentVersion !== undefined || jsonRules.length > 0;
  const ready = !isLoading && alreadyLoaded;

  // NOTE: This is a hack until we add proper stable ids to each rule.
  const resolvedRef = useRef<{
    ruleId: string | undefined;
    index: number | undefined;
  } | null>(null);
  if (ready && resolvedRef.current?.ruleId !== ruleId) {
    if (isEditPolicy) {
      const idx = jsonRules.findIndex((rule) => hashRule(rule) === ruleId);
      resolvedRef.current = {
        ruleId,
        index: idx === -1 ? undefined : idx,
      };
    } else {
      resolvedRef.current = { ruleId: undefined, index: undefined };
    }
  }
  const ruleIndex = resolvedRef.current?.index;

  // Compute the prefill rule once on mount — creating a new object each
  // render would cause RoutingRuleEditorProvider to loop.
  const [prefillRule] = useState(() => {
    const prefill = toPrefill(location.state);
    if (!prefill) return undefined;
    return {
      name: prefill.name,
      disabled: prefill.disabled ?? false,
      requestor: prefill.requestor ?? { type: "any" as const },
      resource: prefill.resource ?? DEFAULT_NEW_RESOURCE,
      approval: prefill.approval ?? [],
    };
  });

  const [initialResourceMode] = useState(() => toResourceMode(location.state));

  const initialRule =
    ruleIndex !== undefined ? jsonRules[ruleIndex] : prefillRule;

  if (isLoading || (!alreadyLoaded && errors.length === 0)) {
    return (
      <Spacing asChild mt={64}>
        <Spin size="large" className="flex justify-center" />
      </Spacing>
    );
  }

  if (errors.length > 0 && !alreadyLoaded) {
    return (
      <Spacing asChild m={24}>
        <Alert
          type="error"
          message="Unable to load policies"
          description={errors.join("\n")}
          showIcon
          className="whitespace-pre-wrap"
        />
      </Spacing>
    );
  }

  if (isEditPolicy && ruleIndex === undefined) {
    return (
      <Spacing asChild m={24}>
        <Alert
          type="error"
          message="Rule not found"
          description="The policy you are trying to edit does not exist. It may have been deleted or the URL is invalid."
          showIcon
        />
      </Spacing>
    );
  }

  return (
    <InstalledIntegrationsProvider>
      <RoutingRuleEditorProvider
        key={ruleId ?? "new"}
        initialRule={initialRule}
      >
        <PolicyEditorPageInner
          initialRule={initialRule}
          ruleIndex={ruleIndex}
          initialResourceMode={initialResourceMode}
        />
      </RoutingRuleEditorProvider>
    </InstalledIntegrationsProvider>
  );
};
